import React, { useEffect, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql } from '@apollo/client'
import { saveCompanyConfiguration } from '../../../apollo'
import { Box, Typography, Input, Button, Alert } from '@mui/material'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'

const SAVE_COMPANY_CONFIGURATION = gql`
  ${saveCompanyConfiguration}
`

// The issuer block printed at the top of every generated report/invoice.
const FIELDS = [
  { key: 'companyName', label: 'Company name', placeholder: 'Borku Ltd' },
  {
    key: 'companyAddress',
    label: 'Company address',
    placeholder: 'Street, City, Postcode'
  },
  { key: 'companyPhone', label: 'Telephone', placeholder: '0344 243 7777' },
  { key: 'companyFax', label: 'Fax (optional)', placeholder: '0845 299 1026' },
  { key: 'vatNumber', label: 'VAT number', placeholder: '123456789' }
]

function CompanyConfiguration(props) {
  const { t } = props
  const formRef = useRef()
  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const [mutate, { loading }] = useMutation(SAVE_COMPANY_CONFIGURATION, {
    onCompleted: () => {
      setErrorMessage('')
      setSuccessMessage('Configuration saved successfully!')
    },
    onError: error => {
      setSuccessMessage('')
      setErrorMessage(
        error?.message || 'An error occurred while saving configuration.'
      )
    }
  })

  useEffect(() => {
    if (!successMessage && !errorMessage) return
    const timeoutId = setTimeout(() => {
      setSuccessMessage('')
      setErrorMessage('')
    }, 3000)
    return () => clearTimeout(timeoutId)
  }, [successMessage, errorMessage])

  const onSave = event => {
    event.preventDefault()
    if (loading) return
    const configurationInput = FIELDS.reduce((input, field) => {
      input[field.key] =
        formRef.current?.[`input-${field.key}`]?.value?.trim() ?? ''
      return input
    }, {})
    mutate({ variables: { configurationInput } })
  }

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            {t('Company Details')}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.form}>
        <form ref={formRef}>
          {FIELDS.map(field => (
            <Box key={field.key} mb={1}>
              <Typography className={classes.labelText}>
                {t(field.label)}
              </Typography>
              <Input
                style={{ marginTop: -1 }}
                id={`input-${field.key}`}
                name={`input-${field.key}`}
                placeholder={field.placeholder}
                type="text"
                defaultValue={props[field.key] || ''}
                disableUnderline
                className={globalClasses.input}
              />
            </Box>
          ))}
          <Box mt={2}>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={onSave}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(CompanyConfiguration)
